import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table"
import { updateJourneySelectedAddresses } from '@store/slice/pilotSlice'
import { updateActiveRowCall } from "@store/slice/sterilizationSlice"
import { useState } from 'react'
import useToggle from '@hook/useToggle'
import Buttons from '@ui/elements/Buttons'
import ScanPhotosModal from './ScanPhotosModal'
import Div from '@ui/layout/Div'
import TextType from '@ui/inputs/TextType'
import ActiveSterilizationCall from './ActiveSterilizationCall'
import useRenderTableScans from '@hook/useRenderTableScans'

const SterilizationCallsListTable = () => {
  const [isPhotosModalActive, togglePhotosModal] = useToggle(false)
  const [modalPhotoFilter, setModalPhotoFilter] = useState(null)
  const [search, setSearch] = useState('')
  const [t] = useTranslation('common')
  const { calls, activeRowCall } = useSelector(state => state.sterilization)
  const { journeySelectedAddresses } = useSelector(state => state.pilot)
  const { mission } = useSelector(state => state.main)
  const { activeSeason } = mission
  const dispatch = useDispatch()
  const renderScans = useRenderTableScans()
  const theme = useTheme([getTheme(), {
    Table: `
        --data-table-library_grid-template-columns: 6% 15% 25% 30% repeat(3, minmax(0, 1fr));
      `,
  },])
  const resize = { resizerHighlight: "#dde2eb", resizerWidth: 5 }

  const activeCalls = calls[activeSeason.id]

  const getNodes = () => {
    const nodes = Object.values(activeCalls).reverse()
    return nodes.filter(call => {
      const address = `${call.sterilizationDistrict}, ${call.sterilizationAddress},${call.sterilizationZipCode}`
      return call.addressResponsibleFullName?.toLowerCase().includes(search.toLowerCase())
        || address.toLowerCase().includes(search.toLowerCase())
        || call.id == search
    })
  }

  const dataSterilizationCalls = { nodes: !calls[activeSeason.id] ? [] : getNodes() }

  const activateRow = call => {
    dispatch(updateActiveRowCall(!activeRowCall || call.id !== activeRowCall.id ? call : null))
  }

  const addToJourneySelectedAddresses = call => {
    dispatch(updateJourneySelectedAddresses({ ...journeySelectedAddresses, [call.id]: call }))
  }

  const handleSearchField = (event) => {
    setSearch(event.target.value)
  }

  const removeFromJourneySelectedAddresses = callId => {
    const list = { ...journeySelectedAddresses }
    delete list[callId]
    dispatch(updateJourneySelectedAddresses({ ...list }))
  }

  return (
    <>
      <TextType value={search || ''} onChange={handleSearchField} placeholder={t('labels.search_field_sterilization_calls')} />
      <Table data={dataSterilizationCalls} theme={theme} layout={{ custom: true }} className='pilot-addresses'>
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell resize={resize}>ID</HeaderCell>
                <HeaderCell resize={resize}>{t('table.FullName')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Address')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Treatment')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.Date')}</HeaderCell>
                <HeaderCell resize={resize}>{t('table.CallerType')}</HeaderCell>
                <HeaderCell resize={resize}>Actions</HeaderCell>
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map(item =>
                <Row
                  key={item.id}
                  item={item}
                  onClick={() => activateRow(item)}
                  className={`pilot-address 
                    ${item.scansData.isFirstScanned && item.scansData.isSecondScanned
                      ? 'is-full-scanned'
                      : (item.scansData.isSecondScanned ? 'is-second-scanned' : (item.scansData.isFirstScanned ? 'is-first-scanned' : 'non-scanned'))
                    }`}
                >
                  <Cell>#{item.id}</Cell>
                  <Cell>{item.addressResponsibleFullName}</Cell>
                  <Cell>
                    {item.companyName ? `${item.companyName},` : ''}{item.sterilizationDistrict}, {item.sterilizationAddress},{item.sterilizationZipCode}
                  </Cell>
                  <Cell className='scan-cell'>
                    <h4><span>{t('table.signaled_nests')}</span> :{item.nestsNumber}</h4>
                    <h3>{t('table.scan')}</h3>
                    <Div cssClass='columns'>
                      {renderScans(activeSeason.nbScans, item, mission)}
                    </Div>
                  </Cell>
                  <Cell>{item.createdAt}</Cell>
                  <Cell>{t(`table.${item.callType}`)}</Cell>
                  <Cell className='table-actions'>
                    <Buttons buttons={[
                      'undefined' === typeof journeySelectedAddresses[item.id]
                        ? {
                          icon: 'icon-location_off',
                          cssClass: 'is-small is-danger',
                          callback: () => addToJourneySelectedAddresses(item)
                        } : {
                          icon: 'icon-location_on',
                          cssClass: 'is-small is-success',
                          callback: () => removeFromJourneySelectedAddresses(item.id)
                        }
                    ]} />
                  </Cell>
                  {activeRowCall && activeRowCall.id === item.id
                    && <ActiveSterilizationCall
                      sterilizationCall={item}
                      togglePhotosModal={togglePhotosModal}
                      setModalPhotoFilter={setModalPhotoFilter}
                    />}
                </Row>
              )}
            </Body>
          </>
        )}
      </Table>

      {
        activeRowCall
          ? <ScanPhotosModal
            isModalActive={isPhotosModalActive}
            toggleModal={togglePhotosModal}
            photoFilter={modalPhotoFilter}
          />
          : <></>
      }
    </>
  )
}

export default SterilizationCallsListTable